import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import {
  CallEndIcon,
  KeypadIcon,
  MicIcon,
  MicOffIcon,
  PauseIcon,
  PlayIcon,
  SpeakerFilledIcon,
} from '@sharedCR/coreDesignSystem/icons';

import {
  hangupCallAction,
  holdCallAction,
  muteCallAction,
  sendDtmfAction,
  unholdCallAction,
  unmuteCallAction,
} from 'store/system/actions';
import { selectCallState } from 'store/system/selectors';

import { HOLD_DEBOUNCE_MS, PRIMARY_ACTION_ICON_SIZE } from '../../MobileCallSurface.constants';
import {
  controlGridStyles,
  controlLabelStyles,
  hangupButtonStyles,
  primaryActionCellStyles,
  primaryActionsRowStyles,
} from '../../MobileCallSurface.styles';
import CallControlButton from '../CallControlButton';
import DtmfPad from '../DtmfPad';

import { InCallControlsTestIds } from './InCallControls.testIds';

const InCallControls = () => {
  const dispatch = useDispatch();
  const callState = useSelector(selectCallState);
  const { isOnHold } = callState;
  const { isMuted } = callState;
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isDtmfOpen, setIsDtmfOpen] = useState(false);
  const [isHoldPending, setIsHoldPending] = useState(false);
  const holdTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
      }
    },
    [],
  );

  const onToggleMute = useCallback(() => {
    if (isMuted) {
      dispatch(unmuteCallAction());

      return;
    }

    dispatch(muteCallAction());
  }, [dispatch, isMuted]);

  const onToggleHold = useCallback(() => {
    if (isHoldPending) {
      return;
    }

    setIsHoldPending(true);

    if (isOnHold) {
      dispatch(unholdCallAction());
    }

    if (!isOnHold) {
      dispatch(holdCallAction());
    }

    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      setIsHoldPending(false);
    }, HOLD_DEBOUNCE_MS);
  }, [dispatch, isOnHold, isHoldPending]);

  const onToggleKeypad = useCallback(() => {
    setIsDtmfOpen(prev => !prev);
  }, []);

  const onToggleSpeaker = useCallback(() => {
    setIsSpeakerOn(prev => !prev);
  }, []);

  const onDtmfPress = useCallback(
    digit => {
      dispatch(sendDtmfAction(digit));
    },
    [dispatch],
  );

  const onHangup = useCallback(() => {
    dispatch(hangupCallAction());
  }, [dispatch]);

  return (
    <>
      {isDtmfOpen && <DtmfPad onDigitPress={onDtmfPress} />}

      <Box sx={controlGridStyles}>
        <CallControlButton
          icon={isMuted ? MicOffIcon : MicIcon}
          isActive={isMuted}
          label={isMuted ? 'Unmute' : 'Mute'}
          onPress={onToggleMute}
        />
        <CallControlButton
          icon={isOnHold ? PlayIcon : PauseIcon}
          isActive={isOnHold}
          isDisabled={isHoldPending}
          label={isOnHold ? 'Resume' : 'Hold'}
          onPress={onToggleHold}
        />
        <CallControlButton
          icon={KeypadIcon}
          isActive={isDtmfOpen}
          label="Keypad"
          onPress={onToggleKeypad}
        />
        <CallControlButton
          icon={SpeakerFilledIcon}
          isActive={isSpeakerOn}
          label="Speaker"
          onPress={onToggleSpeaker}
        />
      </Box>

      <Box sx={primaryActionsRowStyles}>
        <Box sx={primaryActionCellStyles}>
          <Box
            aria-label="Hang up"
            component="button"
            data-testid={InCallControlsTestIds.HANGUP}
            sx={hangupButtonStyles}
            onClick={onHangup}
          >
            <CallEndIcon size={PRIMARY_ACTION_ICON_SIZE} />
          </Box>
          <Typography sx={controlLabelStyles} variant="regular12">
            End
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default memo(InCallControls);
